import { BitkitDefinitionTooltip, BitkitIconComponent, IconArrowUp, IconWarning } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Portal } from '@chakra-ui/react/portal';
import { Text } from '@chakra-ui/react/text';
import { Tooltip } from '@chakra-ui/react/tooltip';
import { ReactNode } from 'react';

type RunnableMetaBaseProps = {
  defaultTooltipLabel: ReactNode;
  icon: BitkitIconComponent;
  status?: 'warning' | 'increasing';
};

type RunnableMetaProps = RunnableMetaBaseProps &
  (
    | {
        data?: ReactNode;
        definitionButton?: undefined;
        definitionText?: undefined;
      }
    | {
        /** Plain text whenever a definition may be set, since the definition's term is plain text. */
        data?: string | number;
        /** Action at the end of the definition, e.g. a link to the matching Insights page. */
        definitionButton?: { href?: string; label: string };
        /** Turns the value into a definition term that explains itself on click, for a warning or a change. */
        definitionText?: string;
      }
  );

const RunnableMeta = (props: RunnableMetaProps) => {
  const { data, defaultTooltipLabel, definitionButton, definitionText, icon: Icon, status } = props;
  if (data === undefined) {
    return null;
  }

  const renderContent = (value: ReactNode) => (
    <Box alignItems="center" color="text/secondary" display="flex" gap="4" paddingInlineStart="4" whiteSpace="nowrap">
      <Icon color="icon/tertiary" size="16" />
      {value}
      {status === 'increasing' && <IconArrowUp color="icon/negative" size="16" />}
      {status === 'warning' && <IconWarning color="icon/tertiary" size="16" />}
    </Box>
  );

  return (
    <Box
      display="flex"
      paddingInlineEnd="12"
      _before={{
        content: '""',
        width: '1px',
        height: '100%',
        backgroundColor: 'border/minimal',
        marginInlineEnd: '8',
      }}
      _first={{
        _before: { marginInlineEnd: 0, width: 0 },
      }}
    >
      {definitionText ? (
        renderContent(
          <BitkitDefinitionTooltip
            button={
              definitionButton?.href ? { children: definitionButton.label, href: definitionButton.href } : undefined
            }
            text={definitionText}
            textSize="md"
            truncate={false}
          >
            {String(data)}
          </BitkitDefinitionTooltip>,
        )
      ) : (
        // Bitkit registers `tooltip` as a slot recipe, so these primitives arrive already styled.
        // BitkitTooltip is this same composition; `defaultTooltipLabel` is a ReactNode its
        // `text: string` cannot carry.
        <Tooltip.Root
          disabled={!defaultTooltipLabel}
          positioning={{ placement: 'top', offset: { mainAxis: 4, crossAxis: 0 } }}
        >
          <Tooltip.Trigger asChild>
            {renderContent(
              <Text as="span" textStyle="body/md/regular">
                {data}
              </Text>,
            )}
          </Tooltip.Trigger>
          <Portal>
            <Tooltip.Positioner>
              <Tooltip.Content>
                <Tooltip.Arrow>
                  <Tooltip.ArrowTip />
                </Tooltip.Arrow>
                {defaultTooltipLabel}
              </Tooltip.Content>
            </Tooltip.Positioner>
          </Portal>
        </Tooltip.Root>
      )}
    </Box>
  );
};

export default RunnableMeta;
